import { useEffect, useState } from "react";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { ApplianceFreshness } from "./model";

/** Appliances per fork request - each resolves the golden upstream once. */
const FORK_BATCH_SIZE = 3;
/** Fork requests in flight at once. Every one fans out to GitLab. */
const FORK_CONCURRENCY = 2;

interface ForkState {
  vpc: string;
  allFresh: boolean;
  results: ApplianceFreshness["results"];
  forkLinks: ApplianceFreshness["forkLinks"];
}

export interface UseWaveAppliancesResult {
  /** `null` until the roster arrived. */
  appliances: ApplianceFreshness[] | null;
  /** The roster request itself is in flight. */
  loading: boolean;
  /** Appliances whose fork state is still being checked. */
  forksPending: number;
}

/**
 * The appliances of a wave, loaded in two steps: the roster (deployment, PRTG,
 * contacts - no fork comparison) renders at once, then the fork states are
 * filled in progressively, a few appliances per request. A batch that fails
 * leaves its appliances with `forksChecked: false` - shown as unknown, never as
 * fresh. The server re-checks forks itself when the MR is created.
 */
export const useWaveAppliances = (
  wave: string | null,
  enabled: boolean = true,
): UseWaveAppliancesResult => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const [appliances, setAppliances] = useState<ApplianceFreshness[] | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [forksPending, setForksPending] = useState<number>(0);

  useEffect(() => {
    const token = keycloak?.token;
    if (!enabled || !wave || typeof token !== "string") return;
    let cancelled = false;
    setAppliances(null);
    setForksPending(0);
    setLoading(true);

    const base = `v2/${AnketaResourcesEnum.opsanio}/waves/${encodeURIComponent(
      wave,
    )}/appliances`;

    const run = async () => {
      const { success, data } = await Get<ApplianceFreshness[]>({
        path: `${base}?forks=0`,
        responseType: "json",
        token,
      });
      if (cancelled) return;
      const rows = ((data as any)?.data ?? data) as ApplianceFreshness[] | null;
      const roster = success && Array.isArray(rows) ? rows : [];
      setAppliances(roster);
      setLoading(false);

      const pending = roster.filter((a) => !a.forksChecked).map((a) => a.vpc);
      setForksPending(pending.length);
      const batches: string[][] = [];
      for (let i = 0; i < pending.length; i += FORK_BATCH_SIZE) {
        batches.push(pending.slice(i, i + FORK_BATCH_SIZE));
      }

      let cursor = 0;
      const worker = async () => {
        while (!cancelled && cursor < batches.length) {
          const batch = batches[cursor++];
          const query = batch
            .map((vpc) => `vpc=${encodeURIComponent(vpc)}`)
            .join("&");
          const resp = await Get<ForkState[]>({
            path: `${base}/forks?${query}`,
            responseType: "json",
            token,
          });
          if (cancelled) return;
          const states = Array.isArray(resp.data) ? resp.data : [];
          if (resp.success && states.length > 0) {
            const byVpc = new Map(states.map((s) => [s.vpc, s]));
            setAppliances((prev) =>
              (prev ?? []).map((a) => {
                const s = byVpc.get(a.vpc);
                return s
                  ? {
                      ...a,
                      allFresh: s.allFresh,
                      results: s.results,
                      forkLinks: s.forkLinks,
                      forksChecked: true,
                    }
                  : a;
              }),
            );
          }
          setForksPending((n) => Math.max(0, n - batch.length));
        }
      };
      await Promise.all(
        Array.from(
          { length: Math.min(FORK_CONCURRENCY, batches.length) },
          worker,
        ),
      );
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wave, enabled, keycloak?.token]);

  return { appliances, loading, forksPending };
};
